import { Injectable, signal } from "@angular/core";

import { BehaviorSubject, Observable } from "rxjs";

type Theme = "dark" | "light";

@Injectable({
  providedIn: "root"
})
export class ThemeService {
  // Initialize with a default value; this will be overwritten in initializeTheme()
  readonly currentTheme = signal<Theme>("dark");

  // Subject to track theme changes
  private readonly _themeChangedSubject = new BehaviorSubject<Theme>("dark");
  readonly themeChanged$: Observable<Theme> = this._themeChangedSubject.asObservable();

  private readonly _themeKey = "theme";

  initializeTheme(): void {
    // Check if a theme is stored in localStorage
    const storedTheme = localStorage.getItem(this._themeKey) as Theme;
    if (storedTheme) {
      this.setTheme(storedTheme, false);
    } else {
      // If no stored theme, detect browser's preferred color scheme
      const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      this.setTheme(prefersDark ? "dark" : "light", false);
    }
  }

  setTheme(theme: Theme, saveTheme = true): void {
    // Update the current theme signal
    this.currentTheme.set(theme);

    // Emit the theme change event
    this._themeChangedSubject.next(theme);

    // Set the data-theme attribute on the body element
    document.documentElement.setAttribute("data-theme", theme);

    // Optionally save the theme to localStorage
    if (saveTheme) {
      localStorage.setItem(this._themeKey, theme);
    }
  }

  toggleTheme(): void {
    // Toggle between 'dark' and 'light'
    const newTheme: Theme = this.currentTheme() === "dark" ? "light" : "dark";
    this.setTheme(newTheme);
  }

  isDarkTheme(): boolean {
    return this.currentTheme() === "dark";
  }

  isLightTheme(): boolean {
    return this.currentTheme() === "light";
  }
}
